import { useState } from "react";
import { Copy } from "lucide-react";
import { Card } from "@/components/kit";
import { formatGal, formatToEighths } from "@/lib/tank";
import { useT } from "@/lib/i18n";
import { ChartSource } from "@/components/ChartSource";
import { DropWarning, ProductTag, WaterWarning, WrongProduct } from "@/components/drop/DropWarning";

const COLOR = { safe: "#34D399", caution: "#F59E0B", critical: "#F87171" };

function readout(s, r, sf, t, mismatchText) {
  const label = [t({ overflow: "SPILL RISK", over95: "DO NOT DROP - PAST 95% SHUTOFF", over90: "STOP - OVER 90%", mismatch: "WRONG PRODUCT" }[sf.level] || "OK to drop"),
    mismatchText && sf.level !== "mismatch" && t("WRONG PRODUCT")].filter(Boolean).join(" + ");
  return [
    label,
    t("{c} gal, {d}\" tank", { c: formatGal(s.capacity), d: s.diameter }),
    t("Now {i} ({g} gal)", { i: formatToEighths(s.startInches), g: formatGal(r.startGallons) }),
    t("Drop {g} gal", { g: formatGal(s.dropGallons) }),
    t("After {i} ({g} gal)", { i: formatToEighths(r.finalInches), g: formatGal(r.finalGallons) }),
  ].join("\n");
}

export function ResultCard({ s, r, sf, mismatchText }) {
  const [copied, setCopied] = useState(false);
  const t = useT();
  if (!(s.dropGallons > 0)) return (
    <Card testID="result-card" className="result-card result-empty">
      <div className="label">{t("Stick after the drop")}</div>
      <div className="empty-result" data-testid="result-empty">{t("Type your stick now and the gallons to drop below. The stick after the drop shows here.")}</div>
      <ChartSource s={s} r={r} />
    </Card>
  );
  const copy = async () => {
    try { await navigator.clipboard.writeText(readout(s, r, sf, t, mismatchText)); } catch { /* clipboard blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <Card testID="result-card" className={`result-card result-${sf.level === "ok" ? "safe" : "critical"}`}>
      <div className="result-top">
        <div className="label">{t("Stick after the drop")}</div>
        <button type="button" className="copy-btn" data-testid="copy-button" onClick={copy}>
          <Copy size={14} />{copied ? t("Copied") : t("Copy")}
        </button>
      </div>
      <ProductTag id={s.product} testid="result-product" />
      <DropWarning sf={sf} mismatchText={mismatchText} testid="status-message" />
      {sf.level !== "mismatch" && <WrongProduct text={mismatchText} testid="status-product-warning" />}
      <WaterWarning s={s} testid="result-water-warning" />
      <div className="final-stick" data-testid="final-stick" style={{ color: sf.level === "ok" ? COLOR.safe : COLOR.critical }}>{formatToEighths(r.finalInches)}</div>
      <div className="result-sub mono" data-testid="final-sub">
        +{r.stickGain.toFixed(2)}" · {formatGal(r.finalGallons)} gal · {t("{p}% full", { p: r.finalPct.toFixed(1) })}
      </div>
      <ChartSource s={s} r={r} />
    </Card>
  );
}
